'use client';

/**
 * AnnouncementsPanel — the user's "more info" / release-notes surface.
 *
 * Renders every published announcement targeted at the current user
 * (including previously dismissed ones — the archive), grouped into
 * "What's new" (release_notes) and "Notices" (everything else), each
 * expandable into the full body + feature highlights + CTA.
 *
 * Embedded by AnnouncementToasts (as the modal content) and by the
 * 'updates' tab in ManagedTasksModal.
 */

import React, { useCallback, useEffect, useState } from 'react';
import {
  Bell, ChevronDown, ChevronRight, ExternalLink, Info, Loader2,
  Megaphone, Sparkles, Undo2, Wrench,
} from 'lucide-react';
import { apiFetch } from '@/lib/apiFetch';
import type { AnnouncementKind, AnnouncementSeverity, UserAnnouncement } from '@/lib/announcements/types';

const KIND_META: Record<AnnouncementKind, { label: string; color: string; icon: React.ElementType }> = {
  general: { label: 'Announcement', color: '#6b7280', icon: Megaphone },
  system: { label: 'System notice', color: '#3b82f6', icon: Info },
  release_notes: { label: "What's new", color: '#8b5cf6', icon: Sparkles },
  maintenance: { label: 'Maintenance', color: '#f59e0b', icon: Wrench },
};

const SEVERITY_COLOR: Record<AnnouncementSeverity, string> = {
  info: '#3b82f6',
  success: '#10b981',
  warning: '#f59e0b',
  critical: '#ef4444',
};

interface Props {
  /** When set, that announcement renders auto-expanded at the top. */
  focusId?: string | null;
  /** Called after list loads — e.g. so a badge can show the unread count. */
  onLoaded?: (items: UserAnnouncement[]) => void;
}

export default function AnnouncementsPanel({ focusId, onLoaded }: Props) {
  const [items, setItems] = useState<UserAnnouncement[] | null>(null);
  const [error, setError] = useState(false);
  const [expanded, setExpanded] = useState<Set<string>>(() => new Set(focusId ? [focusId] : []));

  const load = useCallback(async () => {
    try {
      const res = await apiFetch('/api/v1/announcements?include_dismissed=true&limit=100');
      const d = await res.json();
      if (d.success) {
        setItems(d.data);
        onLoaded?.(d.data);
      } else {
        setError(true);
      }
    } catch {
      setError(true);
    }
  }, [onLoaded]);

  // eslint-disable-next-line react-hooks/set-state-in-effect -- async fetch; setState lands in .then, not synchronously
  useEffect(() => { void load(); }, [load]);

  // Hosts (ManagedTasksModal refresh button, undismiss elsewhere) trigger a
  // reload via the shared refresh event.
  useEffect(() => {
    const onRefresh = () => void load();
    window.addEventListener('ta:announcements-refresh', onRefresh);
    return () => window.removeEventListener('ta:announcements-refresh', onRefresh);
  }, [load]);

  const toggle = useCallback((id: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }, []);

  const undismiss = useCallback(async (id: string) => {
    await apiFetch(`/api/v1/announcements/${id}`, {
      method: 'POST',
      body: JSON.stringify({ action: 'undismiss' }),
    }).catch(() => undefined);
    window.dispatchEvent(new CustomEvent('ta:announcements-refresh'));
    void load();
  }, [load]);

  const renderItem = (a: UserAnnouncement) => {
    const meta = KIND_META[a.kind] ?? KIND_META.general;
    const MetaIcon = meta.icon;
    const isExpanded = expanded.has(a.id);
    const wasDismissed = Boolean(a.dismissed_at);
    return (
      <div
        key={a.id}
        className="overflow-hidden rounded-xl border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800/80"
      >
        <button
          type="button"
          onClick={() => toggle(a.id)}
          className="flex w-full items-center gap-3 px-4 py-3 text-left"
        >
          <span
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-white"
            style={{ backgroundColor: SEVERITY_COLOR[a.severity] ?? meta.color }}
          >
            <MetaIcon className="h-4 w-4" />
          </span>
          <span className="min-w-0 flex-1">
            <span className="block truncate text-sm font-semibold text-gray-900 dark:text-gray-100">
              {a.title}
            </span>
            <span className="block text-xs text-gray-500 dark:text-gray-400">
              {meta.label}
              {a.release_version ? ` · v${a.release_version}` : ''}
              {a.published_at ? ` · ${new Date(a.published_at).toLocaleDateString()}` : ''}
              {wasDismissed ? ' · dismissed' : ''}
            </span>
          </span>
          {isExpanded
            ? <ChevronDown className="h-4 w-4 shrink-0 text-gray-400" />
            : <ChevronRight className="h-4 w-4 shrink-0 text-gray-400" />}
        </button>

        {isExpanded && (
          <div className="border-t border-gray-100 px-4 pb-4 pt-3 text-sm text-gray-700 dark:border-gray-700 dark:text-gray-300">
            {a.summary && <p className="mb-2 font-medium">{a.summary}</p>}
            {Array.isArray(a.highlights) && a.highlights.length > 0 && (
              <ul className="mb-3 space-y-1.5">
                {a.highlights.map((h, i) => (
                  <li key={i} className="flex gap-2">
                    <Sparkles className="mt-0.5 h-3.5 w-3.5 shrink-0 text-violet-500" />
                    <span>
                      <strong>{h.title}</strong>
                      {h.description ? ` — ${h.description}` : ''}
                    </span>
                  </li>
                ))}
              </ul>
            )}
            {a.body && <p className="whitespace-pre-wrap">{a.body}</p>}
            <div className="mt-3 flex flex-wrap items-center gap-2">
              {a.action_url && (
                <a
                  href={a.action_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 rounded-lg bg-blue-600 px-3 py-1.5 text-xs font-semibold text-white transition hover:bg-blue-700"
                >
                  {a.action_label || 'Learn more'} <ExternalLink className="h-3 w-3" />
                </a>
              )}
              {wasDismissed && (
                <button
                  type="button"
                  onClick={() => void undismiss(a.id)}
                  className="inline-flex items-center gap-1.5 rounded-lg border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-700 transition hover:bg-gray-50 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700"
                >
                  <Undo2 className="h-3 w-3" /> Show popup again
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    );
  };

  if (items === null && !error) {
    return (
      <div className="flex items-center justify-center py-12 text-gray-400">
        <Loader2 className="mr-2 h-5 w-5 animate-spin" /> Loading updates…
      </div>
    );
  }
  if (error) {
    return <div className="py-12 text-center text-sm text-gray-500">Couldn&apos;t load announcements.</div>;
  }
  if (!items || items.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center px-4 py-12 text-center">
        <Bell className="mb-2 h-10 w-10 text-gray-300 dark:text-gray-600" />
        <p className="text-sm font-medium text-gray-700 dark:text-gray-200">No announcements yet</p>
        <p className="mt-1 max-w-sm text-xs text-gray-500 dark:text-gray-400">
          Platform notices and feature release notes will appear here.
        </p>
      </div>
    );
  }

  const focused = focusId ? items.filter((a) => a.id === focusId) : [];
  const releases = items.filter((a) => a.kind === 'release_notes' && a.id !== focusId);
  const notices = items.filter((a) => a.kind !== 'release_notes' && a.id !== focusId);

  return (
    <div className="space-y-4">
      {focused.length > 0 && (
        <section className="space-y-2">{focused.map(renderItem)}</section>
      )}
      {releases.length > 0 && (
        <section>
          <h4 className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-violet-600 dark:text-violet-300">
            <Sparkles className="h-3.5 w-3.5" /> What&apos;s new
          </h4>
          <div className="space-y-2">{releases.map(renderItem)}</div>
        </section>
      )}
      {notices.length > 0 && (
        <section>
          <h4 className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
            <Info className="h-3.5 w-3.5" /> Notices
          </h4>
          <div className="space-y-2">{notices.map(renderItem)}</div>
        </section>
      )}
    </div>
  );
}
